/* eslint-disable react/sort-comp, no-continue, no-restricted-syntax, jsx-a11y/media-has-caption */

import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  addTween,
  prefetchComplete,
  setPlayAll,
} from "../../redux/actions/appUtils";
import { getAudioSource } from "../../helper/URLHelper";
import PlayerMedia from "./player-media";
import { PlayerAudioCallbackContext } from "./player-context";

class PlayerAudioComponent extends React.Component {
  constructor(props) {
    super(props);

    this.cleanupAudioContext = this.cleanupAudioContext.bind(this);
    this.initializeAudioPlayer = this.initializeAudioPlayer.bind(this);
    this.playPauseAudio = this.playPauseAudio.bind(this);
    this.prepareAudioNode = this.prepareAudioNode.bind(this);
    this.prepareAudioContext = this.prepareAudioContext.bind(this);
    this.assignMediaRef = this.assignMediaRef.bind(this);
    this.setGlobalVolumeToElements = this.setGlobalVolumeToElements.bind(this);

    this.AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioContext = null;

    this.mediaRef = {};
    this.audioNodes = {};

    this.playerCallbacks = {
      assignMediaRef: this.assignMediaRef,
      playPauseAudio: this.playPauseAudio,
    };
  }

  componentDidMount() {
    if (this.props.isPlaying) {
      this.initializeAudioPlayer();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isPlaying !== prevProps.isPlaying) {
      this.cleanupAudioContext();
    }

    if (this.props.isPlaying !== prevProps.isPlaying && this.props.isPlaying) {
      this.initializeAudioPlayer();
    }

    if (this.props.globalVolume !== prevProps.globalVolume) {
      this.setGlobalVolumeToElements(this.props.globalVolume);
    }
  }

  componentWillUnmount() {
    this.cleanupAudioContext();
  }

  cleanupAudioContext() {
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.audioNodes = {};
  }

  initializeAudioPlayer() {
    this.audioContext = new this.AudioContext({ sampleRate: 44100 }); // sample rate might affect music visualizer animation, keep it equal to render
    try {
      this.prepareAudioContext();
      this.props.prefetchComplete(this.props.prefetchToken);
    } catch (error) {
      // if an error is catched here, probably there is an issue in handling audio context
      this.props.setPlayAll(false);
    }
  }

  setGlobalVolumeToElements(globalVolume) {
    try {
      for (const [id, item] of this.props.audios.entrySeq()) {
        try {
          if (this.mediaRef[id] && this.audioNodes[id]) {
            const { gainNode } = this.audioNodes[id];
            if (item.get("fadeDetails")) {
              // For fade details, gainNode is already handling the fade, just update base volume
              this.mediaRef[id].volume = globalVolume;
            } else {
              // Use gainNode for volume control (iOS compatibility)
              const volumeValue = item.get("volume") * globalVolume;
              gainNode.gain.setValueAtTime(volumeValue, 0);
              this.mediaRef[id].volume = 1;
            }
          }
        } catch (error) { }
      }
      for (const [id, item] of this.props.videos.entrySeq()) {
        try {
          if (this.mediaRef[id] && this.audioNodes[id]) {
            const { gainNode } = this.audioNodes[id];
            // Use gainNode for volume control (iOS compatibility)
            const volumeValue = item.get("volume") * globalVolume;
            gainNode.gain.setValueAtTime(volumeValue, 0);
            this.mediaRef[id].volume = 1;
          }
        } catch (error) { }
      }
    } catch (error) { }
  }

  playPauseAudio(params = {}) {
    const { id, playhead, mediaType = "audio" } = params;

    try {
      const audioId = id;
      const audio = this.mediaRef[audioId];

      if (audio && !this.audioNodes[audioId]) {
        // to handle frame/grid videos
        this.prepareAudioNode(audioId);
      }

      const audioNode = this.audioNodes[audioId];
      const audioCtx = this.audioContext;

      let audioItem = this.props.audios.get(audioId);
      let mediaStartKey = "musicStart";
      if (mediaType === "video") {
        audioItem = this.props.videos.get(audioId);
        mediaStartKey = "videoStart";
      }

      if (!audioNode || !audio || !audioCtx || !audioItem) {
        return;
      }

      const playStart = audioItem.get("playStart");
      const playEnd = audioItem.get("playEnd");

      const { srcNode } = audioNode;
      let { gainNode } = audioNode;

      // disconnect old gain node to cleanup old scheduled fade
      gainNode.disconnect(audioCtx.destination);
      srcNode.disconnect(gainNode);

      // create new node to schedule fade
      gainNode = audioCtx.createGain();
      srcNode.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      audioNode.gainNode = gainNode;

      if (playhead >= playStart && playhead < playEnd) {
        const ctxTime = audioCtx.currentTime;
        const defaultAmplitude = audioItem.get("defaultAmplitude") ? audioItem.get("defaultAmplitude") : 1;
        const fadeDetails = audioItem.get("fadeDetails");
        const musicStart = parseFloat(audioItem.get(mediaStartKey));

        if (fadeDetails) {
          let addedFadeIdx = 0;
          for (let fadeIdx = 0; fadeIdx < fadeDetails.size; fadeIdx += 1) {
            const fadeDetail = fadeDetails.get(fadeIdx);
            const time = parseFloat(fadeDetail.get("t"));
            const amplitude = parseFloat(fadeDetail.get("a"));

            const fadeTime = playStart + (time - musicStart);
            if (fadeTime < playStart || fadeTime > playEnd || fadeTime < playhead) {
              continue;
            }

            const localTime = fadeTime - playhead;
            const scheduleTime = ctxTime + localTime;

            if (scheduleTime < 0) {
              continue;
            }

            if (addedFadeIdx === 0) {
              gainNode.gain.setValueAtTime(amplitude, 0);
              if (ctxTime !== 0) {
                gainNode.gain.setValueAtTime(amplitude, scheduleTime);
              }
            } else {
              const prevFadeDetail = fadeDetails.get(fadeIdx - 1);
              const prevAmplitude = parseFloat(prevFadeDetail.get("a"));
              if (amplitude === prevAmplitude) {
                gainNode.gain.setValueAtTime(amplitude, scheduleTime);
              } else {
                gainNode.gain.linearRampToValueAtTime(amplitude, scheduleTime);
              }
            }

            addedFadeIdx += 1;
          }

          if (addedFadeIdx === 0 && fadeDetails.size !== 0) {
            const fadeDetail = fadeDetails.get(fadeDetails.size - 1);
            const amplitude = parseFloat(fadeDetail.get("a"));
            gainNode.gain.setValueAtTime(amplitude, 0);
          }
          if (fadeDetails.size === 0) {
            gainNode.gain.setValueAtTime(defaultAmplitude, 0);
          }

          audio.volume = this.props.globalVolume;
        } else {
          // Use gainNode for volume control to support iOS Safari
          // iOS doesn't respect audio.volume property, it's controlled by hardware buttons
          const volumeValue = audioItem.get("volume") * this.props.globalVolume;
          gainNode.gain.setValueAtTime(volumeValue, 0);
          audio.volume = 1; // Set to max, let gainNode control the volume
        }
      }
    } catch (error) {
      // errors happening here might be fatal
      // example: audio volume is increased in first loop and second loop failed to decrease it
      // this scenario will not happen in most cases as we pause audio before applying gain, but just in case...
      this.props.setPlayAll(false);
    }
  }

  prepareAudioNode(audioId) {
    const audioCtx = this.audioContext;
    const audio = this.mediaRef[audioId];

    if (!audio || !audioCtx) {
      // frame/grid videos might mount later
      return;
    }

    const srcNode = audioCtx.createMediaElementSource(audio);
    const gainNode = audioCtx.createGain();
    srcNode.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    this.audioNodes[audioId] = { srcNode, gainNode };
  }

  prepareAudioContext() {
    this.props.audios.keySeq().forEach(this.prepareAudioNode);
    this.props.videos.keySeq().forEach(this.prepareAudioNode);
  }

  assignMediaRef(r, id) {
    if (r) {
      this.mediaRef[id] = r;
    } else {
      delete this.mediaRef[id];
    }
  }

  render() {
    const audioElList = [];

    this.props.audios.valueSeq().forEach((audioItem) => {
      const audioId = audioItem.get("id");
      const { src } = getAudioSource({
        item: {
          type: audioItem.get("type"),
          subType: audioItem.get("subType"),
          src: audioItem.get("src"),
        },
        isFreeUser: true /** @todo check based on user plan */,
      });

      audioElList.push(
        <PlayerMedia
          key={audioId}
          from="player"
          id={audioId}
          src={src}
          playStart={audioItem.get("playStart")}
          playEnd={audioItem.get("playEnd")}
          mediaStart={audioItem.get("musicStart")}
          mediaEnd={audioItem.get("musicEnd")}
          speed={audioItem.get("speed")}
          mediaType="audio"
          allowCustomCaching={audioItem.get("type") !== "PIXABAY"}
        />
      );
    });

    return (
      <PlayerAudioCallbackContext.Provider value={this.playerCallbacks}>
        {this.props.children}
        {audioElList}
      </PlayerAudioCallbackContext.Provider>
    );
  }
}

PlayerAudioComponent.propTypes = {
  audios: PropTypes.object,
  videos: PropTypes.object,
  prefetchToken: PropTypes.string,
  prefetchComplete: PropTypes.func,
  isPlaying: PropTypes.bool,
  setPlayAll: PropTypes.func,
  globalVolume: PropTypes.number,
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

const mapStateToProps = (state) => ({
  runningState: state.app.get("runningState"),
  isPlayAll: state.app.get("isPlayAll"),
  isPlaying: state.app.get("isPlayAll"),
  prefetchToken: state.app.get("prefetchToken"),
  isLoaded: state.app.get("isLoaded"),
  globalVolume: state.app.get("globalVolume"),
});

const mapDispatchToProps = (dispatch) => ({
  prefetchComplete: (token, count) => dispatch(prefetchComplete(token, count)),
  addTween: (data, tweenType) => dispatch(addTween(data, tweenType)),
  setPlayAll: (data) => dispatch(setPlayAll(data)),
});

const PlayerAudio = connect(mapStateToProps, mapDispatchToProps)(PlayerAudioComponent);

export default PlayerAudio;
